import { inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, interval, map, of, startWith, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs';

import { TasksCountService } from '../services/tasks-count.service';
import { TasksService } from '../services/tasks.service';
import { createTasksSearchPayload } from '../utils/tasks-adapter.functions';
import { SystemUsersService } from '@lib/core/services/system-users.service';

import * as TaskActions from './task.actions';
import {
  selectTasksContinuationToken,
  selectTasksFilter,
  selectTasksPageSize,
} from './task.selectors';

export const setTasksFilterEffect = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(TaskActions.setTasksFilter),
      map(() => TaskActions.fetchTasksFirstPage())
    );
  },
  { functional: true }
);

export const fetchTasksFirstPageEffect = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) => {
    return actions$.pipe(
      ofType(TaskActions.fetchTasksFirstPage),
      tap(() => store.dispatch(TaskActions.clearTasksList())),
      withLatestFrom(store.select(selectTasksFilter), store.select(selectTasksPageSize)),
      map(([, filter, pageSize]) => {
        const payload = createTasksSearchPayload({ pageSize, filter, continuationToken: '' });
        return TaskActions.getTasksList({ payload });
      })
    );
  },
  { functional: true }
);

export const fetchTasksNextPageEffect = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) => {
    return actions$.pipe(
      ofType(TaskActions.fetchTasksNextPage),
      withLatestFrom(store.select(selectTasksFilter), store.select(selectTasksPageSize), store.select(selectTasksContinuationToken)),
      map(([, filter, pageSize, continuationToken]) => {
        const payload = createTasksSearchPayload({ pageSize, filter, continuationToken });
        return TaskActions.getTasksList({ payload });
      })
    );
  },
  { functional: true }
);

export const getTasksListEffect = createEffect(
  (actions$ = inject(Actions), tasksService = inject(TasksService)) => {
    return actions$.pipe(
      ofType(TaskActions.getTasksList),
      switchMap(({ payload }) =>
        tasksService.searchTasks(payload).pipe(
          map(response => TaskActions.getTasksListSuccess({ response })),
          catchError(error => of(TaskActions.getTasksListFailure({ error })))
        )
      )
    );
  },
  { functional: true }
);

export const startTaskEffect = createEffect(
  (actions$ = inject(Actions), tasksService = inject(TasksService)) => {
    return actions$.pipe(
      ofType(TaskActions.startTask),
      switchMap(({ task }) =>
        tasksService.startTask(task.id, task.createdAt).pipe(
          map(updatedTask => TaskActions.startTaskSuccess({ task: updatedTask })),
          catchError(error => of(TaskActions.startTaskFailure({ task, error })))
        )
      )
    );
  },
  { functional: true }
);

export const assignTaskEffect = createEffect(
  (actions$ = inject(Actions), tasksService = inject(TasksService)) => {
    return actions$.pipe(
      ofType(TaskActions.assignTask),
      switchMap(({ task, payload }) =>
        tasksService.assignTask(task.id, task.createdAt, payload).pipe(
          map(updatedTask => TaskActions.assignTaskSuccess({ task: updatedTask })),
          catchError(error => of(TaskActions.assignTaskFailure({ task, error })))
        )
      )
    );
  },
  { functional: true }
);

export const resolveTaskEffect = createEffect(
  (actions$ = inject(Actions), tasksService = inject(TasksService)) => {
    return actions$.pipe(
      ofType(TaskActions.resolveTask),
      switchMap(({ task, payload }) =>
        tasksService.resolveTask(task.id, task.createdAt, payload).pipe(
          map(updatedTask => TaskActions.resolveTaskSuccess({ task: updatedTask })),
          catchError(error => of(TaskActions.resolveTaskFailure({ task, error })))
        )
      )
    );
  },
  { functional: true }
);

export const getCurrentTaskEffect = createEffect(
  (actions$ = inject(Actions), tasksService = inject(TasksService)) => {
    return actions$.pipe(
      ofType(TaskActions.getCurrentTask),
      switchMap(({ id, createdAt }) =>
        tasksService.getTaskById(id, createdAt).pipe(
          map(task => TaskActions.getCurrentTaskSuccess({ task })),
          catchError(error => of(TaskActions.getCurrentTaskFailure({ error })))
        )
      )
    );
  },
  { functional: true }
);

export const newTasksCountPollingEffect = createEffect(
  (actions$ = inject(Actions), tasksCountService = inject(TasksCountService)) => {
    return actions$.pipe(
      ofType(TaskActions.startNewTasksCountPolling),
      switchMap(() =>
        interval(60_000).pipe(
          startWith(0),
          switchMap(() =>
            tasksCountService.getNewTasksCount().pipe(
              map(count => TaskActions.getNewTasksCountSuccess({ count })),
              catchError(error => of(TaskActions.getNewTasksCountFailure({ error })))
            )
          ),
          takeUntil(actions$.pipe(ofType(TaskActions.stopNewTasksCountPolling)))
        )
      )
    );
  },
  { functional: true }
);

export const refreshNewTasksCountEffect = createEffect(
  (actions$ = inject(Actions), tasksCountService = inject(TasksCountService)) => {
    return actions$.pipe(
      ofType(TaskActions.startTaskSuccess, TaskActions.fetchTasksFirstPage),
      switchMap(() =>
        tasksCountService.getNewTasksCount().pipe(
          map(count => TaskActions.getNewTasksCountSuccess({ count })),
          catchError(error => of(TaskActions.getNewTasksCountFailure({ error })))
        )
      )
    );
  },
  { functional: true }
);

export const loadSystemUsersEffect = createEffect(
  (actions$ = inject(Actions), systemUsersService = inject(SystemUsersService)) => {
    return actions$.pipe(
      ofType(TaskActions.loadSystemUsers),
      switchMap(() =>
        systemUsersService.getAll().pipe(
          map(users => TaskActions.loadSystemUsersSuccess({ users })),
          catchError(error => of(TaskActions.loadSystemUsersFailure({ error })))
        )
      )
    );
  },
  { functional: true }
);

export const taskEffects = {
  setTasksFilterEffect,
  fetchTasksFirstPageEffect,
  fetchTasksNextPageEffect,
  getTasksListEffect,
  startTaskEffect,
  assignTaskEffect,
  resolveTaskEffect,
  getCurrentTaskEffect,
  refreshNewTasksCountEffect,
  loadSystemUsersEffect,
};
